import React from 'react';
import { MdDragIndicator } from 'react-icons/md';
import { HiOutlineTrash, HiOutlineExclamationCircle, HiOutlineEllipsisHorizontal } from 'react-icons/hi2';

import InlineText from './inline-text';
import InlinePicker from './inline-picker';
import StepChip from './step-chip';
import AddStepMenu from './add-step-menu';
import {
    ASK_INPUT, RUN_TARGET, RUN_TARGET_LABEL, STEP_KIND, STEP_KIND_META, knowledgeSelection,
} from '../model/playbook-model';

/**
 * One numbered row - "1.2  ASK  [appointment date]" - which is also the editor.
 *
 * Everything a step normally needs is set here: the text is typed in place, and
 * a variable or tool is chosen from a chip that opens a list. Only the extras
 * that would not fit on a line (button options, API headers, extraction schema)
 * live behind the "..." toggle, so the common case never leaves the row.
 */

const asOptions = map => Object.entries(map).map(([value, label]) => ({ value, label }));

/** The step's editable body. */
function body(step, ctx) {
    const cfg = step.config || {};
    const { set, slotOptions, lookups, onCreateVariable } = ctx;

    const variablePicker = (field, placeholder) => (
        <InlinePicker
            tone="variable"
            prefix="Aa"
            placeholder={placeholder}
            value={cfg[field]}
            options={slotOptions}
            onChange={v => set({ [field]: v })}
            onCreate={onCreateVariable}
            createLabel="Create variable"
        />
    );

    switch (step.kind) {
        case STEP_KIND.SEND:
            return (
                <InlineText
                    multiline
                    placeholder="Write the message the customer sees"
                    value={firstText(cfg)}
                    onChange={v => set({ displayTextOptions: [{ displayText: v }] })}
                />
            );

        case STEP_KIND.ASK:
            return (
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
                    <InlineText
                        multiline
                        className="flex-1"
                        placeholder={cfg.inputType === ASK_INPUT.DOCUMENT
                            ? 'What should the customer upload?'
                            : 'What should the agent ask?'}
                        value={firstText(cfg)}
                        onChange={v => set({ displayTextOptions: [{ displayText: v }] })}
                    />
                    <span className="shrink-0 text-[12.5px] text-gray-400">into</span>
                    {variablePicker('slot', 'variable')}
                </div>
            );

        case STEP_KIND.SET:
            return (
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
                    {variablePicker('slotToAssign', 'variable')}
                    <span className="shrink-0 text-[12.5px] text-gray-400">=</span>
                    <InlineText
                        className="flex-1"
                        placeholder="a value, or {another_variable}"
                        value={cfg.value}
                        onChange={v => set({ value: v })}
                    />
                </div>
            );

        case STEP_KIND.RUN: {
            const target = cfg.target || RUN_TARGET.PROMPT;
            return (
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
                    <InlinePicker
                        tone="plain"
                        searchable={false}
                        value={target}
                        options={asOptions(RUN_TARGET_LABEL)}
                        onChange={v => set({ target: v })}
                    />

                    {target === RUN_TARGET.PROMPT && (
                        <>
                            <InlineText
                                multiline
                                className="flex-1"
                                placeholder="Describe what the agent should do"
                                value={cfg.action}
                                onChange={v => set({ action: v })}
                            />
                            <span className="shrink-0 text-[12.5px] text-gray-400">into</span>
                            {variablePicker('slotToAssign', 'variable')}
                        </>
                    )}

                    {target === RUN_TARGET.FUNCTION && (
                        <InlinePicker
                            tone="tool"
                            placeholder="pick a function"
                            value={cfg.codehookId}
                            options={(lookups?.codehookList || []).map(c => ({ value: c.id, label: c.name || c.id }))}
                            onChange={v => set({ codehookId: v })}
                        />
                    )}

                    {target === RUN_TARGET.API && (
                        <>
                            <InlinePicker
                                tone="plain"
                                searchable={false}
                                value={cfg.webhookConfig?.method || 'POST'}
                                options={['GET', 'POST', 'PUT', 'DELETE']}
                                onChange={v => set({ webhookConfig: { ...cfg.webhookConfig, method: v } })}
                            />
                            <InlineText
                                className="flex-1 font-mono text-[13px]"
                                placeholder="https://api.example.com/orders/{order_id}"
                                value={cfg.webhookConfig?.url}
                                onChange={v => set({ webhookConfig: { ...cfg.webhookConfig, url: v } })}
                            />
                        </>
                    )}

                    {target === RUN_TARGET.KNOWLEDGE && (
                        <>
                            <InlineText
                                multiline
                                className="flex-1"
                                placeholder="Do you deliver to {city}?"
                                value={cfg.inputPrompt}
                                onChange={v => set({ inputPrompt: v })}
                            />
                            <span className="shrink-0 text-[12.5px] text-gray-400">from</span>
                            {knowledgePicker(cfg, set, lookups?.knowledgeList)}
                            <span className="shrink-0 text-[12.5px] text-gray-400">into</span>
                            {variablePicker('slotToAssign', 'variable')}
                        </>
                    )}
                </div>
            );
        }

        case STEP_KIND.GO_TO:
            return (
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                    <InlinePicker
                        tone="plain"
                        placeholder="Choose a section"
                        value={cfg.sectionId}
                        options={lookups?.sections || []}
                        onChange={sectionId => set({ sectionId })}
                    />
                    <span className="text-xs text-gray-400">Continue from this section</span>
                </div>
            );

        case STEP_KIND.RUN_PLAYBOOK:
            return (
                <InlinePicker
                    tone="tool"
                    placeholder="pick a playbook"
                    value={cfg.journeyId}
                    options={(lookups?.journeyList || []).map(j => ({ value: j.id, label: j.name || j.id }))}
                    onChange={v => set({ journeyId: v })}
                />
            );

        case STEP_KIND.HANDOFF:
            return <span className="text-[14px] text-gray-500">Hand the conversation to a person</span>;

        case STEP_KIND.IF:
            return (
                <span className="text-[14px] text-gray-500">
                    {(step.branches || []).length} branch{(step.branches || []).length === 1 ? '' : 'es'}
                </span>
            );

        default:
            return null;
    }
}

// Sentinel for "no filter" - an empty id would read as an unset picker.
const ALL_KNOWLEDGE = '__all__';

/**
 * Which knowledge bases a RUN step searches, chosen in the row. Each pick
 * toggles one in or out, so several can be chosen as in the canvas; "All"
 * clears the filter.
 */
function knowledgePicker(cfg, set, knowledgeList = []) {
    const ids = cfg.filterKb || [];
    const byId = new Map(knowledgeList.map(k => [k.id, k]));
    const summary = ids.length === 0 ? 'all knowledge bases'
        : ids.map(id => byId.get(id)?.label || id).join(', ');
    return (
        <InlinePicker
            tone="tool"
            ariaLabel="Choose knowledge bases"
            value={ids.length ? null : ALL_KNOWLEDGE}
            trigger={<StepChip tone="tool" value={summary} mono={false} />}
            options={[
                { value: ALL_KNOWLEDGE, label: 'All knowledge bases' },
                ...knowledgeList.map(k => ({ value: k.id, label: `${ids.includes(k.id) ? '✓ ' : ''}${k.label || k.id}` })),
            ]}
            onChange={v => {
                const next = v === ALL_KNOWLEDGE ? []
                    : ids.includes(v) ? ids.filter(id => id !== v) : [...ids, v];
                set(knowledgeSelection(next, knowledgeList));
            }}
        />
    );
}

function firstText(cfg) {
    return (cfg.displayTextOptions || []).map(o => o?.displayText).find(Boolean) || '';
}

/** Whether this step has extras worth a "..." toggle. */
export function hasMore(step) {
    if (step.kind === STEP_KIND.ASK) return true;                     // answer type, retry text
    if (step.kind === STEP_KIND.RUN) return true;                     // every target has delivery options
    return false;
}

export default function StepRow({
    step,
    readOnly = false,
    number,
    expanded,
    errors = [],
    lookups,
    slotOptions,
    onToggleMore,
    onRemove,
    onUpdate,
    onChangeKind,
    onCreateVariable,
    draggable = true,
    onDragStart,
    onDragOver,
    onDrop,
    onDragEnd,
    dragging,
}) {
    if (readOnly) {
        const meta = STEP_KIND_META[step.kind] || {};
        const cfg = step.config || {};
        const text = (cfg.displayTextOptions || []).map(item => item.displayText).join(' / ');
        let summary = text;
        if (step.kind === STEP_KIND.SET) summary = `${cfg.slotToAssign} = ${cfg.value}`;
        if (step.kind === STEP_KIND.RUN) summary = cfg.target === RUN_TARGET.PROMPT ? cfg.action
            : cfg.target === RUN_TARGET.KNOWLEDGE ? cfg.inputPrompt
            : cfg.target === RUN_TARGET.API ? `${cfg.webhookConfig?.method || 'POST'} ${cfg.webhookConfig?.url || ''}` : cfg.codehookId;
        if (step.kind === STEP_KIND.GO_TO) summary = lookups?.sections?.find(section => section.id === cfg.sectionId)?.title || cfg.sectionId;
        if (step.kind === STEP_KIND.RUN_PLAYBOOK) summary = cfg.journeyId;
        if (step.kind === STEP_KIND.HANDOFF) summary = 'Transfer to a human agent';
        if (step.kind === STEP_KIND.IF) summary = 'Check conditions';
        return <div className="flex items-start gap-2.5 rounded-lg px-3 py-3">
            <span className="w-9 shrink-0 text-right text-[13px] tabular-nums text-gray-400">{number}</span>
            <span className={`shrink-0 rounded-md px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${meta.verbClass} ${meta.verbBg}`}>{meta.verb}</span>
            <div className="min-w-0 flex-1 break-words text-sm text-gray-800">
                <span className="whitespace-pre-wrap">{summary || step.label || 'No details'}</span>
                {step.kind === STEP_KIND.ASK && <span className="ml-2 inline-block rounded-md bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">{cfg.slot}</span>}
            </div>
        </div>;
    }
    const meta = STEP_KIND_META[step.kind] || {};
    const hasErrors = errors.length > 0;

    const ctx = {
        set: patch => onUpdate(step.id, patch),
        slotOptions,
        lookups,
        onCreateVariable,
    };

    const showMore = hasMore(step);

    return (
        <div
            draggable={draggable}
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            onDrop={onDrop}
            onDragEnd={onDragEnd}
            title={hasErrors ? errors.join('\n') : undefined}
            className={`group relative flex items-start gap-2.5 rounded-lg py-3 pl-1.5 pr-2
                transition-colors duration-100
                ${hasErrors ? 'bg-amber-50/40' : 'hover:bg-gray-50/80'}
                ${dragging ? 'opacity-40' : ''}`}
        >
            <MdDragIndicator
                className="mt-1 shrink-0 cursor-grab text-gray-300 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
                size={16}
            />

            <span className="mt-1 w-9 shrink-0 text-right text-[13px] tabular-nums text-gray-400">
                {number}
            </span>

            {step.kind === STEP_KIND.IF ? (
                <span className={`mt-0.5 shrink-0 rounded-md px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${meta.verbClass} ${meta.verbBg}`}>
                    {meta.verb}
                </span>
            ) : (
                <span className="mt-0.5 shrink-0">
                    <AddStepMenu
                        ariaLabel={`Change step ${number} type`}
                        allowIf={false}
                        trigger={
                            <span className={`inline-flex rounded-md px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${meta.verbClass} ${meta.verbBg}`}>
                                {meta.verb}
                            </span>
                        }
                        onAdd={kind => onChangeKind(step.id, kind)}
                    />
                </span>
            )}

            <div className="flex min-w-0 flex-1 items-start py-0.5">
                {body(step, ctx)}
            </div>

            {hasErrors && (
                <HiOutlineExclamationCircle
                    className="mt-1 shrink-0 text-amber-500"
                    size={16}
                    title={errors.join('\n')}
                />
            )}

            {showMore && (
                <button
                    type="button"
                    onClick={() => onToggleMore(step.id)}
                    className={`mt-0.5 shrink-0 rounded-md p-1 transition
                        ${expanded
                            ? 'bg-gray-200/70 text-gray-600'
                            : 'text-gray-300 opacity-0 hover:bg-gray-100 hover:text-gray-600 group-hover:opacity-100 group-focus-within:opacity-100'}`}
                    aria-label="More settings"
                    title="More settings"
                >
                    <HiOutlineEllipsisHorizontal size={16} />
                </button>
            )}

            <button
                type="button"
                onClick={() => onRemove(step.id)}
                className="mt-0.5 shrink-0 rounded-md p-1 text-gray-300 opacity-0 transition hover:bg-rose-50 hover:text-rose-600 group-hover:opacity-100 group-focus-within:opacity-100"
                aria-label="Delete step"
            >
                <HiOutlineTrash size={15} />
            </button>
        </div>
    );
}
